import _ from 'lodash';
import { createReducer } from 'redux-act';
import Immutable from 'seamless-immutable';
import { publishingWizardModes } from '../../common/enums';
import PublishingDestinationsActions from './publishing-destinations-actions';

const wizardInitialState = Immutable({
  data: {
    selectedChannelId:       null,
    selectedDisplayUnitId:   null,
    selectedPinnedContainer: null,
    dimensions:              {
      xDimension: '',
      yDimension: '',
    },
    selectedDestinationTypeId: null,
    publishDate:               null,
    expiryDate:                null,
    privacyMgmt:               null,
    privacyConfig:             null,
    lightboxConfig:            null,
    isStaging:                 false,
  },
  isSaving:              false,
  isSaveSuccess:         false,
  isSaveError:           false,
  mode:                  publishingWizardModes.HIDDEN,
  publishingDestination: {},
});

const initialState = Immutable({
  isEditorVisible:             false,
  isLoading:                   true,
  initialLoad:                 true,
  hasFetched:                  false,
  activeTab:                   undefined,
  activePublishingDestination: undefined,
  allIds:                      [],
  removingIds:                 [],
  isOverlayModalVisible:       false,
  overlayContainer:            null,
  isEditorConfigVisible:       true,
  ...wizardInitialState,
});

const reducer = createReducer((on) => {
  on(PublishingDestinationsActions.changeEditModalTab, (state, { activeTab }) => state.set('activeTab', activeTab));

  on(PublishingDestinationsActions.fetchPublishingDestinations, (state) => state.set('isLoading', true));

  on(PublishingDestinationsActions.fetchPublishingDestinationsSuccess, (state, { publishingDestinations }) =>
    state.merge({
      initialLoad: false,
      isLoading:   false,
      hasFetched:  true,
      allIds:      _.map(publishingDestinations, (pd) => pd.id),
    })
  );

  on(PublishingDestinationsActions.createPublishingDestinationSuccess, (state, { publishingDestination }) =>
    state.set('allIds', [...state.allIds, publishingDestination.id]).set('publishingDestination', publishingDestination)
  );

  on(PublishingDestinationsActions.fetchPublishingDestinationsFailure, (state) => state.set('isLoading', false));

  on(PublishingDestinationsActions.hidePublishingDestinationEditor, (state) =>
    state.merge({ isEditorVisible: false, activePublishingDestination: null })
  );

  on(PublishingDestinationsActions.showPublishingDestinationEditor, (state, { publishingDestination, activeTab }) =>
    state.merge({
      isEditorVisible:             true,
      activePublishingDestination: publishingDestination,
      activeTab:                   activeTab,
      isEditorConfigVisible:       true,
    })
  );

  on(PublishingDestinationsActions.removePublishingDestination, (state, { id }) =>
    state.set('removingIds', [...state.removingIds, id])
  );

  on(PublishingDestinationsActions.removePublishingDestinationSuccess, (state, { publishingDestinationData }) =>
    state
      .set(
        'allIds',
        _.reject(state.allIds, (_id) => _id === publishingDestinationData)
      )
      .set(
        'removingIds',
        _.reject(state.removingIds, (_id) => _id === publishingDestinationData)
      )
  );

  on(PublishingDestinationsActions.removePublishingDestinationFailure, (state, { publishingDestinationData }) =>
    state.set(
      'removingIds',
      _.reject(state.removingIds, (_id) => _id === publishingDestinationData)
    )
  );

  on(PublishingDestinationsActions.setActivePublishingDestination, (state, { publishingDestination }) => {
    return state.set('activePublishingDestination', publishingDestination);
  });

  on(PublishingDestinationsActions.updateActivePublishingDestination, (state, { publishingDestination }) => {
    const _activePublishingDestination = state.activePublishingDestination.merge(publishingDestination, { deep: true });
    return state.set('activePublishingDestination', _activePublishingDestination);
  });

  on(PublishingDestinationsActions.updateLightboxConfig, (state, { config }) => {
    return state.setIn(['activePublishingDestination', 'displayContainer', 'displayConfig', 'lightboxConfig'], config);
  });

  on(PublishingDestinationsActions.updateWebEmbedConfig, (state, { config }) => {
    return state.setIn(['activePublishingDestination', 'displayContainer', 'displayConfig', 'webEmbed'], config);
  });

  on(PublishingDestinationsActions.showOverlay, (state, { overlayContainer }) => {
    return state.set('overlayContainer', overlayContainer).set('isOverlayModalVisible', true);
  });

  on(PublishingDestinationsActions.closeOverlay, (state) => {
    return state.set('overlayContainer', null).set('isOverlayModalVisible', false);
  });

  on(PublishingDestinationsActions.changeEditorConfigVisibility, (state, { isEditorConfigVisible }) => {
    return state.set('isEditorConfigVisible', isEditorConfigVisible);
  });

  on(PublishingDestinationsActions.selectChannel, (state, { id }) =>
    state.setIn(['data', 'selectedChannelId'], _.parseInt(id, 10))
  );

  on(PublishingDestinationsActions.selectPrivacyManagement, (state, { privacyMgmt }) =>
    state.setIn(['data', 'privacyMgmt'], privacyMgmt)
  );

  on(PublishingDestinationsActions.updatePrivacyManagementConfig, (state, { privacyConfig }) =>
    state.setIn(['data', 'privacyConfig'], privacyConfig)
  );

  on(PublishingDestinationsActions.selectDisplayUnit, (state, { id }) =>
    state.setIn(['data', 'selectedDisplayUnitId'], _.parseInt(id, 10))
  );

  on(PublishingDestinationsActions.selectPinnedContainer, (state, { pinnedContainer }) =>
    state.setIn(['data', 'selectedPinnedContainer'], pinnedContainer)
  );

  on(PublishingDestinationsActions.setDimensions, (state, { xDimension, yDimension }) =>
    state.setIn(['data', 'dimensions'], { xDimension, yDimension })
  );

  on(PublishingDestinationsActions.selectDestinationType, (state, { id }) =>
    state.setIn(['data', 'selectedDestinationTypeId'], id)
  );

  on(PublishingDestinationsActions.setSchedulingDates, (state, { publishDate, expiryDate }) =>
    state.setIn(['data', 'publishDate'], publishDate).setIn(['data', 'expiryDate'], expiryDate)
  );

  on(PublishingDestinationsActions.showWizard, (state, { mode, seedData }) => {
    const data = state.data.merge(seedData);
    return state.merge({ mode, data });
  });

  on(PublishingDestinationsActions.resetWizard, (state, { isStaging }) =>
    state.set('data', initialState.data).setIn(['data', 'isStaging'], isStaging)
  );

  on(
    [
      PublishingDestinationsActions.cancelAddDestinationWizard,
      PublishingDestinationsActions.finishSaving,
      PublishingDestinationsActions.hideWizard,
    ],
    (state) => {
      return state.merge({ ...wizardInitialState });
    }
  );

  on(PublishingDestinationsActions.savePublishingDestination, (state) => state.set('isSaving', true));

  on(PublishingDestinationsActions.savePublishingDestinationSuccess, (state) =>
    state.set('isSaving', false).set('isSaveSuccess', true)
  );

  on(PublishingDestinationsActions.savePublishingDestinationFailure, (state) =>
    state.set('isSaving', false).set('isSaveFailure', true)
  );

  on(PublishingDestinationsActions.setLightboxConfig, (state, { config }) =>
    state.setIn(['data', 'lightboxConfig'], config)
  );
}, initialState);

export default reducer;
